import { parseIsoOffset, toIsoLocal } from "./dateTime";
import { isSettableKind } from "./types";
import type { DiscoveredKind, FieldOption, FieldValue, FieldValueElement, RejectionDetail } from "./types";

export type CoerceResult = { ok: true; value: FieldValue } | { ok: false; reason: "invalid" | "unsupported"; detail: RejectionDetail };

export interface CoerceTarget {
  kind: DiscoveredKind;
  format?: "epoch-ms";
  /** The full option list — membership is decided here, never against the capped snapshot. */
  options?: FieldOption[];
}

function invalid(detail: RejectionDetail): CoerceResult {
  return { ok: false, reason: "invalid", detail };
}

function toNumber(value: FieldValue): number | undefined {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    return Number(value);
  }
  return undefined;
}

function coerceInteger(value: FieldValue): CoerceResult {
  const number = toNumber(value);
  return number !== undefined && Number.isSafeInteger(number) ? { ok: true, value: number } : invalid("not-integer");
}

function coerceNumber(value: FieldValue): CoerceResult {
  const number = toNumber(value);
  return number !== undefined && Number.isFinite(number) ? { ok: true, value: number } : invalid("not-number");
}

function coerceBoolean(value: FieldValue): CoerceResult {
  if (typeof value === "boolean") {
    return { ok: true, value };
  }
  if (value === "true" || value === "false") {
    return { ok: true, value: value === "true" };
  }
  return invalid("not-boolean");
}

// Option values are ids that cross number/string on the wire, so membership is loose; the
// option's own value is what gets applied, so the field receives its native type.
function coerceOption(value: FieldValue, options: FieldOption[] | undefined): CoerceResult {
  if (!options) {
    return invalid("no-options");
  }
  const match = options.find((option) => option.value == value);
  return match ? { ok: true, value: match.value } : invalid("not-an-option");
}

// Matched as text, the way the server matches them: 12 and "12" name the same option. Every
// element must be an option, and a value named twice is selected once.
function coerceSelection(value: FieldValue, options: FieldOption[] | undefined): CoerceResult {
  if (!Array.isArray(value)) {
    return invalid("not-an-array");
  }
  if (!options) {
    return invalid("no-options");
  }
  const optionValues = options.map((option) => option.value).filter((optionValue): optionValue is FieldValueElement => optionValue != null);
  const matches = value
    .map((element) => optionValues.find((optionValue) => String(optionValue) === String(element)))
    .filter((match): match is FieldValueElement => match !== undefined);
  return matches.length === value.length ? { ok: true, value: Array.from(new Set(matches)) } : invalid("not-an-option");
}

function coerceTags(value: FieldValue): CoerceResult {
  if (!Array.isArray(value)) {
    return invalid("not-an-array");
  }
  return value.every((element) => typeof element === "string") ? { ok: true, value: [...value] } : invalid("not-a-string");
}

// Re-spelled in the browser's own offset, so the applied value reads back exactly as the picker
// reports it and a later comparison sees the same text.
function coerceDateTime(value: FieldValue): CoerceResult {
  const epochMillis = typeof value === "string" ? parseIsoOffset(value) : undefined;
  const local = epochMillis === undefined ? undefined : toIsoLocal(epochMillis);
  return local ? { ok: true, value: local } : invalid("not-a-date-time");
}

/** Turns the model's value into what the field's own change path expects, or says why it cannot. */
export function coerceForKind(value: FieldValue, target: CoerceTarget): CoerceResult {
  if (!isSettableKind(target.kind)) {
    return { ok: false, reason: "unsupported", detail: "unsettable-kind" };
  }
  if (target.kind === "multiselect") {
    return coerceSelection(value, target.options);
  }
  if (target.kind === "tags") {
    return coerceTags(value);
  }
  // Loose option matching would otherwise let [1] through as 1.
  if (Array.isArray(value)) {
    return invalid("not-a-single-value");
  }
  if (target.kind === "datetime") {
    return coerceDateTime(value);
  }
  if (target.format === "epoch-ms") {
    return coerceInteger(value);
  }
  switch (target.kind) {
    case "integer":
      return coerceInteger(value);
    case "float":
    case "number":
      return coerceNumber(value);
    case "checkbox":
      return coerceBoolean(value);
    case "select":
    case "searchable-select":
      return coerceOption(value, target.options);
    default:
      return { ok: true, value: value == null ? "" : String(value) };
  }
}
